// hook to filter items in list
import { useCallback, useState } from 'react'
import { filterOnlyUnique, sortAscending } from '../helpers'
import { useRouter } from 'next/router'

export interface SelectedItem {
  property: string;
  selected: any[];
  filterType: FilterType
}

export interface FilterSelectedValue {
  property: string;
  value: any[] | any;
  filterType: FilterType
}

export enum FilterType {
  Checkbox = 'checkbox',
  Slider = 'slider',
  Boolean = 'boolean',
}

export interface FilterCondition<T> {
  property: string;
  group?: string;
  filterType: FilterType;
  condition?: (item: T, selected: any[]) => boolean;
}

export enum FilterResultType {
  Rim = 'rim',
  Tyre = 'tyre',
}

interface ReducedObject {
  [key: string]: any;
}

export const useFilter = (
  items: any[],
  conditions: FilterCondition<any>[] | null
) => {
  const router = useRouter()
  const [selectedValues, setSelectedValues] = useState<SelectedItem[]>([])

  /**
   * Saves selected values to state
   * @param param0 
   */
  const saveSelectedValues = ({ property, filterType, value }: FilterSelectedValue) => {
    const selectedItem = selectedValues.find(item => item.property === property)
    const simpleValue = filterType === FilterType.Slider || filterType === FilterType.Boolean

    if (selectedItem) {
      let selected = simpleValue ? selectedItem.selected : [...selectedItem.selected]

      if (filterType === FilterType.Slider) {
        selected = value
      }

      // handle add or remove of selected value
      if (filterType == FilterType.Checkbox) {
        if (selected.includes(value)) {
          const index = selected.indexOf(value)
          selected.splice(index, 1)
        } else {
          selected.push(value)
        }
      }

      if (filterType === FilterType.Boolean) {
        selected = value
      }

      // save selected values to state
      setSelectedValues([
        ...selectedValues.filter(item => item.property !== property),
        {
          property,
          selected,
          filterType,
        },
      ])
    } else {
      // add new selected value to state
      setSelectedValues([
        ...selectedValues,
        {
          property,
          selected: simpleValue ? value : [value],
          filterType,
        },
      ])
    }
  }

  /**
   * Get filter query object from selected values
   * @returns filter query object
   */
  const getFilterQuery = (): ReducedObject => {
    if (!selectedValues.length) return {}

    const items = selectedValues.reduce<ReducedObject>((acc, item) => {
      if (typeof item.selected === 'object')
        acc['f_' + item.property] = item.selected.join(',')
      else
        acc['f_' + item.property] = item.selected
      return acc
    }, {})

    return items
  }

  /**
   * Set selected values from query string
   */
  const setFilterFromQuery = () => {
    const query = router.query
    const generatedValues = Object.keys(query)
      .filter(key => key.startsWith('f_'))
      .map(key => {
        if (!query[key]) return null

        const property = key.replace('f_', '')
        const selected = query[key] as string

        // convert selected values to numbers if possible, if true or false, convert to boolean, otherwise keep as string
        const selectedValues = selected.split(',').map(value => {
          if (value === 'true' || value === 'false') {
            return value === 'true'
          }

          if (!isNaN(Number(value))) {
            return Number(value)
          }

          return value
        })

        // find filterType from conditions
        const condition = conditions?.find(condition => condition.property === property)

        let filterType: FilterType | null = null

        // Try to figure out filter type from condition or selected values
        // Note: Recommended to set all filter conditons instead of doing this
        if (condition?.filterType) {
          filterType = condition.filterType
        } else if (selectedValues.length === 2 && typeof selectedValues[0] === 'number' && typeof selectedValues[1] === 'number') {
          filterType = FilterType.Slider
        } else if (selectedValues.length === 1 && typeof selectedValues[0] === 'boolean') {
          filterType = FilterType.Boolean
        } else {
          filterType = FilterType.Checkbox
        }

        return {
          property,
          selected: selectedValues,
          filterType: filterType
        }
      })

    setSelectedValues(generatedValues as SelectedItem[])
  }


  /**
   * Gets selected items by property
   * @param property 
   * @returns 
   */
  const getSelectedValues = (property: string) => {
    const selectedItem = selectedValues.find(item => item.property === property)

    return selectedItem ? selectedItem.selected : []
  }

  /**
    * Checks if property is selected
    * @param property
    * @returns boolean
    */
  const isPropertySelected = (property: string) => {
    const selectedItem = selectedValues.find(item => item.property === property)

    return selectedItem ? !!selectedItem.selected : false
  }

  /**
   * Gets item values by property
   * @param property 
   * @returns array of values
   */
  const getValuesByPropery = (property: string) => {
    return items
      .map(item => item[property])
      .filter(item => item !== null && item !== undefined && item !== '')
      .filter(filterOnlyUnique)
      .sort(sortAscending)
  }

  /**
   * Filters items by selected values and returns list of filtered items
   * @returns 
   */
  const filterItems = useCallback(() => {
    return items.filter(item => {
      return selectedValues.every(selectedItem => {
        const property = selectedItem.property
        const selected = selectedItem.selected
        const settings = conditions?.find(condition => condition.property === property)

        if (!selected || selected?.length === 0) {
          return true
        }

        if (settings?.group && conditions) {
          const groupConditions = getGroup(settings.group, selectedValues, conditions)
          return groupConditions.some(groupItem => runItemCondition(item, selectedItem, groupItem))
        }

        return runItemCondition(item, selectedItem, settings)
      })
    })
  }, [conditions, items, selectedValues])

  const runItemCondition = (item: any, selectedItem: SelectedItem, filterCondition?: FilterCondition<any>) => {
    const property = selectedItem.property
    const selected = selectedItem.selected
    const filterType = selectedItem.filterType

    if (filterCondition && filterCondition.condition) {
      return filterCondition.condition(item, selected)
    }

    if (filterType === FilterType.Boolean) {
      return !selected || item[property] === selected
    } else if (filterType === FilterType.Slider)
      return item[property] >= selected[0] && item[property] <= selected[1]
    else
      return selected.includes(item[property])
  }

  const getGroup = (group: string, selectedValues: SelectedItem[], conditions: FilterCondition<any>[]) => {
    const groupItems = conditions.filter(condition => condition.group === group)

    const selectedGroups = groupItems.filter(groupItem => {
      const selectedItem = selectedValues.find(item => item.property === groupItem.property)

      if (!selectedItem)
        return false

      const selected = selectedItem.selected
      if (!selected || selected?.length === 0) {
        return false
      }

      return true
    })

    return selectedGroups
  }

  const clearSelectedValues = () => {
    setSelectedValues([])
  }

  return {
    getValuesByPropery,
    getSelectedValues,
    isPropertySelected,
    saveSelectedValues,
    clearSelectedValues,
    filterItems,
    selectedValues,
    getFilterQuery,
    setFilterFromQuery,
  }
}